// Phase 5 Part 4 — Admin Role Management panel.
//
// One lane: League Roles — one card per role (President, VP, GS,
// Treasurer, Committee, Admin). Holders + inline "add member" picker.
//
// Team-scoped roles (Captain / Vice Captain) are managed under the
// Teams section instead so team leadership lives next to team roster
// edits. The server still supports team-scoped assignments via
// /api/v1/admin/roles; only the UI here is committee-focused.
//
// Backed by /api/v1/admin/roles. Gated in Admin.jsx to ADMIN /
// PRESIDENT / VICE_PRESIDENT / GENERAL_SECRETARY (server enforces the
// same gate on every write).

import React, { useEffect, useMemo, useState } from "react";
import {
  Shield, Users as UsersIcon, Plus, X,
  Crown, Sparkles, Landmark, ShieldCheck, ScrollText, Gavel, Megaphone
} from "lucide-react";
import { Panel } from "./primitives";
import Avatar from "./Avatar";
import { useToast } from "./Toast";
import { api } from "../api";

const ROLE_ICONS = {
  PRESIDENT:             Crown,
  VICE_PRESIDENT:        Sparkles,
  GENERAL_SECRETARY:     Landmark,
  TREASURER:             Landmark,
  RULES_HEAD:            ScrollText,
  UMPIRING_HEAD:         Gavel,
  SOCIAL_MEDIA_ADMIN:    Megaphone,
  EXEC_COMMITTEE_MEMBER: UsersIcon,
  ADMIN:                 ShieldCheck
};

export default function RoleAssignmentsPanel({ state }) {
  const toast = useToast();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const d = await api.listRoleAssignments();
      setData(d);
    } catch (e) {
      toast.error(e.message || "Couldn't load role assignments.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  // Active members list for the picker. Prefer parent state.members so
  // we don't double-fetch; fall back to whatever the API returned.
  const members = useMemo(() => {
    const src = state?.members || [];
    return src
      .filter(m => m.active !== false)
      .map(m => ({
        id:              m.id,
        name:            m.name || `${m.firstName || ""} ${m.lastName || ""}`.trim(),
        email:           m.email,
        avatarUpdatedAt: m.avatarUpdatedAt
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [state?.members]);

  const holdersFor = (roleCode, teamId = null) =>
    (data?.assignments || []).filter(a =>
      a.roleCode === roleCode &&
      (teamId == null ? a.teamId == null : a.teamId === teamId)
    );

  async function assign(roleCode, userId, teamId = null) {
    if (!userId) return;
    setBusy(true);
    try {
      await api.assignRole({ userId: Number(userId), roleCode, teamId });
      toast.success("Role assigned.");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Couldn't assign role.");
    } finally { setBusy(false); }
  }

  async function unassign(a) {
    const scope = a.teamId ? ` for ${a.teamName || "team"}` : "";
    if (!confirm(`Remove ${a.userName} as ${a.roleName}${scope}?`)) return;
    setBusy(true);
    try {
      await api.unassignRole({ userId: a.userId, roleCode: a.roleCode, teamId: a.teamId });
      toast.success("Removed.");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Couldn't remove.");
    } finally { setBusy(false); }
  }

  if (loading) {
    return (
      <Panel title={<><Shield size={16}/> Role Management</>}>
        <p style={{color:"var(--muted)"}}>Loading role assignments…</p>
      </Panel>
    );
  }
  if (!data) return null;

  return (
    <Panel title={<><Shield size={16}/> Role Management</>}>
      <p className="roleMgr-lead">
        Assign league-wide roles (President, Vice President, General
        Secretary, Treasurer, Committee, Admin) here. Team captains and
        vice captains are managed under the Teams section. Each change
        takes effect immediately — the user's next page load reflects
        the new permissions.
      </p>

      <section className="roleMgr-section">
        <header className="roleMgr-sectionHead">
          <Landmark size={16}/>
          <h4>League Roles</h4>
          <small>Global scope — one holder per role is typical, but multiple are allowed.</small>
        </header>
        {(() => {
          const filled = data.leagueRoles.filter(r => holdersFor(r.code).length > 0);
          const vacant = data.leagueRoles.filter(r => holdersFor(r.code).length === 0);
          return (
            <>
              <div className="roleMgr-grid">
                {filled.map(role => (
                  <RoleSlot
                    key={role.code}
                    role={role}
                    holders={holdersFor(role.code)}
                    members={members}
                    busy={busy}
                    onAssign={userId => assign(role.code, userId)}
                    onRemove={unassign}
                  />
                ))}
              </div>
              {vacant.length > 0 && (
                <VacantSlots
                  roles={vacant}
                  members={members}
                  busy={busy}
                  onAssign={(code, userId) => assign(code, userId)}
                />
              )}
            </>
          );
        })()}
      </section>
    </Panel>
  );
}

function RoleSlot({ role, teamId, holders, members, busy, onAssign, onRemove }) {
  const Icon = ROLE_ICONS[role.code] || Shield;
  const [pick, setPick] = useState("");

  // Exclude members already holding this role at this scope so we don't
  // offer a no-op assignment.
  const alreadyIds = new Set(holders.map(h => h.userId));
  const options = members.filter(m => !alreadyIds.has(m.id));

  function submitPick(e) {
    e.preventDefault();
    if (!pick) return;
    const id = pick;
    setPick("");
    onAssign(id);
  }

  return (
    <article className={`roleMgr-card roleMgr-card--${role.tone || "muted"}`}>
      <header className="roleMgr-cardHead">
        <span className="roleMgr-cardIcon"><Icon size={14}/></span>
        <h5>{role.name}</h5>
        <small>{holders.length} assigned</small>
      </header>

      {holders.length === 0 ? (
        <div className="roleMgr-empty"><em>Vacant</em></div>
      ) : (
        <ul className="roleMgr-holders">
          {holders.map(h => (
            <li key={`${h.userId}-${h.teamId || 0}`}>
              <Avatar member={{ id: h.userId, name: h.userName, avatarUpdatedAt: h.avatarUpdatedAt }} size={28}/>
              <div>
                <b>{h.userName}</b>
                <small>{h.userEmail}</small>
              </div>
              <button
                type="button"
                className="roleMgr-removeBtn"
                title="Remove"
                onClick={() => onRemove(h)}
                disabled={busy}
              >
                <X size={13}/>
              </button>
            </li>
          ))}
        </ul>
      )}

      <form className="roleMgr-picker" onSubmit={submitPick}>
        <select
          value={pick}
          onChange={e => setPick(e.target.value)}
          disabled={busy || options.length === 0}
        >
          <option value="">
            {options.length === 0 ? "No members available" : "Add member…"}
          </option>
          {options.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <button
          type="submit"
          className="roleMgr-addBtn"
          disabled={busy || !pick}
        >
          <Plus size={14}/>
        </button>
      </form>
    </article>
  );
}

// Compact "N vacant · role names" chip that expands to per-role
// pickers on click. Replaces the previous grid of full-height "Vacant"
// cards that used to dominate the panel when the committee wasn't
// fully assembled.
function VacantSlots({ roles, members, busy, onAssign }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="roleMgr-vacant">
      <button
        type="button"
        className="roleMgr-vacant-chip"
        onClick={() => setExpanded(e => !e)}
      >
        <span className="roleMgr-vacant-count">{roles.length} vacant</span>
        <span className="roleMgr-vacant-names">
          {roles.map(r => r.name).join(" · ")}
        </span>
        <span className="roleMgr-vacant-toggle">
          {expanded ? "Hide" : "Assign"}
        </span>
      </button>
      {expanded && (
        <div className="roleMgr-vacant-rows">
          {roles.map(r => (
            <VacantRow
              key={r.code}
              role={r}
              members={members}
              busy={busy}
              onAssign={userId => onAssign(r.code, userId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VacantRow({ role, members, busy, onAssign }) {
  const Icon = ROLE_ICONS[role.code] || Shield;
  const [pick, setPick] = useState("");
  function submit(e) {
    e.preventDefault();
    if (!pick) return;
    const id = pick;
    setPick("");
    onAssign(id);
  }
  return (
    <form className={`roleMgr-vacant-row roleMgr-vacant-row--${role.tone || "muted"}`} onSubmit={submit}>
      <span className="roleMgr-vacant-icon"><Icon size={12}/></span>
      <span className="roleMgr-vacant-label">{role.name}</span>
      <select value={pick} onChange={e => setPick(e.target.value)} disabled={busy}>
        <option value="">Add member…</option>
        {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <button type="submit" className="roleMgr-addBtn" disabled={busy || !pick}>
        <Plus size={14}/>
      </button>
    </form>
  );
}
